'use client';

// Shared "Claim Number Prefixes" settings panel -- used both by the Tenant
// detail page's Settings tab and the Edit Tenant wizard's Settings step, so
// both surfaces stay in sync with one implementation.
//
// A tenant has zero or more claim-number-prefix "groups" (e.g. prefix "09"
// for one branch, "10" for another), each carrying its own list of
// assessment-report recipient emails -- the prefix and its emails are one
// row/unit. Each group also has an Active/Inactive toggle (reversible
// pause) distinct from the trash icon (permanent removal).

import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Hash, Plus, Trash2 } from 'lucide-react';
import apiClient from '@/lib/api-client';

type PrefixRecipient = { id: string; email: string };
type PrefixGroup = {
  id: string;
  claim_number_prefix: string;
  is_active: boolean;
  recipients: PrefixRecipient[];
};

export function TenantClaimSettingsPanel({ tenantId }: { tenantId: string }) {
  const [groups, setGroups] = useState<PrefixGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [newPrefix, setNewPrefix] = useState('');
  const [addingGroup, setAddingGroup] = useState(false);
  const [groupError, setGroupError] = useState<string | null>(null);
  const [removingGroupId, setRemovingGroupId] = useState<string | null>(null);
  const [togglingGroupId, setTogglingGroupId] = useState<string | null>(null);

  const [newEmailByGroup, setNewEmailByGroup] = useState<Record<string, string>>({});
  const [addingEmailFor, setAddingEmailFor] = useState<string | null>(null);
  const [emailErrorByGroup, setEmailErrorByGroup] = useState<Record<string, string | null>>({});
  const [removingRecipientId, setRemovingRecipientId] = useState<string | null>(null);

  useEffect(() => {
    loadGroups();
  }, [tenantId]);

  const loadGroups = async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const data = await apiClient.getTenantClaimPrefixes(tenantId);
      setGroups(data.map((g) => ({
        id: g.id,
        claim_number_prefix: g.claim_number_prefix,
        is_active: g.is_active,
        recipients: g.recipients.map((r) => ({ id: r.id, email: r.email })),
      })));
    } catch (e: any) {
      setLoadError(e.response?.data?.error?.message || 'Failed to load claim settings');
    } finally {
      setLoading(false);
    }
  };

  const handleAddGroup = async () => {
    setGroupError(null);
    if (!/^\d{2}$|^\d{4}$|^\d{6}$/.test(newPrefix)) {
      setGroupError('Prefix must be exactly 2, 4, or 6 digits');
      return;
    }
    setAddingGroup(true);
    try {
      const created = await apiClient.createTenantClaimPrefix(tenantId, newPrefix);
      setGroups((prev) => [...prev, {
        id: created.id,
        claim_number_prefix: created.claim_number_prefix,
        is_active: created.is_active,
        recipients: [],
      }]);
      setNewPrefix('');
    } catch (e: any) {
      setGroupError(e.response?.data?.error?.message || 'Failed to add prefix');
    } finally {
      setAddingGroup(false);
    }
  };

  const handleToggleActive = async (groupId: string, nextActive: boolean) => {
    setTogglingGroupId(groupId);
    setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, is_active: nextActive } : g)));
    try {
      await apiClient.updateTenantClaimPrefix(tenantId, groupId, { is_active: nextActive });
    } catch {
      // Revert on failure — the toggle didn't actually take effect server-side.
      setGroups((prev) => prev.map((g) => (g.id === groupId ? { ...g, is_active: !nextActive } : g)));
    } finally {
      setTogglingGroupId(null);
    }
  };

  const handleRemoveGroup = async (groupId: string) => {
    setRemovingGroupId(groupId);
    try {
      await apiClient.deleteTenantClaimPrefix(tenantId, groupId);
      setGroups((prev) => prev.filter((g) => g.id !== groupId));
    } catch { /* silently ignore */ } finally { setRemovingGroupId(null); }
  };

  const handleAddEmail = async (groupId: string) => {
    const email = (newEmailByGroup[groupId] || '').trim();
    if (!email) return;
    setEmailErrorByGroup((prev) => ({ ...prev, [groupId]: null }));
    setAddingEmailFor(groupId);
    try {
      const created = await apiClient.createTenantPrefixRecipient(tenantId, groupId, email);
      setGroups((prev) => prev.map((g) =>
        g.id === groupId ? { ...g, recipients: [...g.recipients, { id: created.id, email: created.email }] } : g
      ));
      setNewEmailByGroup((prev) => ({ ...prev, [groupId]: '' }));
    } catch (e: any) {
      setEmailErrorByGroup((prev) => ({ ...prev, [groupId]: e.response?.data?.error?.message || 'Failed to add email' }));
    } finally {
      setAddingEmailFor(null);
    }
  };

  const handleRemoveEmail = async (groupId: string, recipientId: string) => {
    setRemovingRecipientId(recipientId);
    try {
      await apiClient.deleteTenantPrefixRecipient(tenantId, groupId, recipientId);
      setGroups((prev) => prev.map((g) =>
        g.id === groupId ? { ...g, recipients: g.recipients.filter((r) => r.id !== recipientId) } : g
      ));
    } catch { /* silently ignore */ } finally { setRemovingRecipientId(null); }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Hash className="h-5 w-5 text-gray-500" /> Claim Number Prefixes
          </CardTitle>
          <CardDescription>
            Each prefix this tenant's claim numbers start with (2, 4, or 6 digits) has its own list of assessment-report recipient emails
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Add a new prefix group */}
          <div className="flex items-end gap-3">
            <div className="space-y-2">
              <Label htmlFor={`new-prefix-${tenantId}`}>New prefix</Label>
              <Input
                id={`new-prefix-${tenantId}`}
                value={newPrefix}
                onChange={(e) => setNewPrefix(e.target.value.replace(/\D/g, '').slice(0, 6))}
                placeholder="e.g. 09"
                className="w-40 font-mono text-sm"
                inputMode="numeric"
              />
            </div>
            <Button onClick={handleAddGroup} disabled={addingGroup || !newPrefix}>
              <Plus className="mr-2 h-4 w-4" />
              Add Prefix
            </Button>
          </div>
          {groupError && <p className="text-sm text-red-600">{groupError}</p>}

          {/* Prefix groups list */}
          {loading ? (
            <div className="space-y-3"><Skeleton className="h-24 w-full" /><Skeleton className="h-24 w-full" /></div>
          ) : loadError ? (
            <p className="text-sm text-red-600">{loadError}</p>
          ) : groups.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              <Hash className="h-10 w-10 mx-auto mb-3 opacity-40" />
              <p className="text-sm">No claim number prefixes added yet.</p>
            </div>
          ) : (
            <div className="space-y-4">
              {groups.map((group) => (
                <div key={group.id} className={`border rounded-lg p-4 ${!group.is_active ? 'bg-gray-50 opacity-70' : ''}`}>
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <span className="font-mono text-sm font-semibold text-gray-900">{group.claim_number_prefix}</span>
                      <Badge variant="outline" className="text-xs">prefix</Badge>
                      {!group.is_active && <Badge variant="secondary" className="text-xs">Inactive</Badge>}
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="flex items-center gap-2">
                        <Switch
                          checked={group.is_active}
                          onCheckedChange={(checked) => handleToggleActive(group.id, checked)}
                          disabled={togglingGroupId === group.id}
                        />
                        <span className="text-xs text-gray-500">{group.is_active ? 'Active' : 'Inactive'}</span>
                      </div>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleRemoveGroup(group.id)}
                        disabled={removingGroupId === group.id}
                        title="Permanently remove this prefix and its emails"
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>

                  <div className="flex items-end gap-2 mb-3">
                    <div className="flex-1 space-y-1">
                      <Label htmlFor={`email-${group.id}`} className="text-xs text-gray-500">Add email</Label>
                      <Input
                        id={`email-${group.id}`}
                        type="email"
                        value={newEmailByGroup[group.id] || ''}
                        onChange={(e) => setNewEmailByGroup((prev) => ({ ...prev, [group.id]: e.target.value }))}
                        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddEmail(group.id); } }}
                        placeholder="reports@example.com"
                        className="text-sm"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={() => handleAddEmail(group.id)}
                      disabled={addingEmailFor === group.id || !(newEmailByGroup[group.id] || '').trim()}
                    >
                      <Plus className="mr-1 h-3.5 w-3.5" />
                      Add
                    </Button>
                  </div>
                  {emailErrorByGroup[group.id] && (
                    <p className="text-sm text-red-600 mb-2">{emailErrorByGroup[group.id]}</p>
                  )}

                  {group.recipients.length === 0 ? (
                    <p className="text-sm text-gray-400">No emails added for this prefix yet.</p>
                  ) : (
                    <div className="divide-y">
                      {group.recipients.map((r) => (
                        <div key={r.id} className="py-2 flex items-center justify-between">
                          <span className="text-sm text-gray-900">{r.email}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleRemoveEmail(group.id, r.id)}
                            disabled={removingRecipientId === r.id}
                          >
                            <Trash2 className="h-3.5 w-3.5 text-red-400" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default TenantClaimSettingsPanel;
